import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CreditCard, Info, AlertCircle, TrendingUp } from "lucide-react";
import { PricingCard } from "@/components/billing/PricingCard";
import { SeatStepper } from "@/components/billing/SeatStepper";
import { SeatManagementDialog } from "@/components/billing/SeatManagementDialog";
import { useSubscription } from "@/hooks/use-subscription";
import { useSeatCount } from "@/hooks/use-seat-count";
import { useAuth } from "@/contexts/AuthContext";
import { formatCurrency, MAX_PRO_SEATS } from "@/lib/billing-utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";

interface TeamMember {
  id: string;
  name: string;
  email: string;
  currentRoles: string[];
  status: 'invited' | 'onboarded';
}

export default function Billing() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const { subscription, loading: subscriptionLoading, trialDaysRemaining, refetch: refetchSubscription } = useSubscription(profile?.company_id);
  const { seatCount, loading: seatLoading, refetch: refetchSeats } = useSeatCount(profile?.company_id);
  const [isSeatDialogOpen, setIsSeatDialogOpen] = useState(false);
  const [tempSeatCount, setTempSeatCount] = useState(10);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Initialize temp seat count from current paid seats
  useEffect(() => {
    if (seatCount.totalPaid > 0) {
      setTempSeatCount(seatCount.totalPaid);
    }
  }, [seatCount.totalPaid]);

  // Fetch team members
  const fetchTeamMembers = async () => {
    if (!profile?.company_id) return;
    
    setLoadingMembers(true);
    try {
      const { data: personnelData, error } = await supabase
        .from('profiles')
        .select(`
          id,
          name,
          email
        `)
        .eq('company_id', profile.company_id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const membersWithRoles = await Promise.all(
        (personnelData || []).map(async (person) => {
          const { data: roles } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', person.id);

          return {
            id: person.id,
            name: person.name,
            email: person.email,
            currentRoles: roles?.map(r => r.role) || [],
            status: 'onboarded' as const,
          };
        })
      );

      // Add invited users
      const { data: invitations } = await supabase
        .from('user_invitations')
        .select('*')
        .eq('company_id', profile.company_id)
        .eq('accepted', false)
        .gt('expires_at', new Date().toISOString());

      const invitedMembers = (invitations || []).map(inv => ({
        id: inv.id,
        name: inv.email,
        email: inv.email,
        currentRoles: [inv.role],
        status: 'invited' as const,
      }));

      setTeamMembers([...membersWithRoles, ...invitedMembers]);
    } catch (error: any) {
      console.error('Error fetching team members:', error);
      toast({
        title: "Error",
        description: "Failed to load team members: " + (error.message || "Unknown error"),
        variant: "destructive"
      });
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleManageSeats = async () => {
    await fetchTeamMembers();
    setIsSeatDialogOpen(true);
  };

  const handleContactSales = () => {
    toast({
      title: "Contact Sales",
      description: "Please email sales@manageaze.com for enterprise pricing.",
    });
  };

  const handleUpgradeToPro = async () => {
    // Fetch team members and open seat management dialog
    await fetchTeamMembers();
    setIsSeatDialogOpen(true);
  };

  const handleSeatChange = () => {
    // Refresh seat count and subscription after changes
    refetchSeats();
    refetchSubscription();
  };

  const currentPlan = subscription?.plan_type || 'trial';
  const isLoading = subscriptionLoading || seatLoading || loadingMembers;

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        {/* Current Plan Banner Skeleton */}
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <Skeleton variant="shimmer" className="h-8 w-48" />
                <Skeleton variant="shimmer" className="h-5 w-64" />
              </div>
              <Skeleton variant="shimmer" className="h-16 w-80" />
            </div>
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <Skeleton variant="shimmer" className="h-4 w-24" />
                <Skeleton variant="shimmer" className="h-4 w-16" />
              </div>
              <Skeleton variant="shimmer" className="h-2 w-full" />
            </div>
          </CardHeader>
        </Card>

        {/* Pricing Cards Skeleton */}
        <div className="grid md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton variant="shimmer" className="h-6 w-24" />
                <Skeleton variant="shimmer" className="h-8 w-32" />
                <Skeleton variant="shimmer" className="h-4 w-full" />
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  {[...Array(4)].map((_, j) => (
                    <Skeleton key={j} variant="shimmer" className="h-4 w-full" />
                  ))}
                </div>
                <Skeleton variant="shimmer" className="h-10 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Seat Usage Summary Skeleton */}
        <Card>
          <CardHeader>
            <Skeleton variant="shimmer" className="h-6 w-32" />
            <Skeleton variant="shimmer" className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="space-y-1">
                  <Skeleton variant="shimmer" className="h-4 w-16" />
                  <Skeleton variant="shimmer" className="h-8 w-12" />
                </div>
              ))}
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <Skeleton variant="shimmer" className="h-4 w-24" />
                <Skeleton variant="shimmer" className="h-8 w-20" />
              </div>
              <Skeleton variant="shimmer" className="h-4 w-48" />
            </div>

            <div className="flex gap-2">
              <Skeleton variant="shimmer" className="h-10 w-32" />
              <Skeleton variant="shimmer" className="h-10 w-36" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Current Plan Banner */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200 dark:from-blue-950/30 dark:to-purple-950/30 dark:border-blue-900">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <CreditCard className="h-6 w-6" />
                Current Plan: {currentPlan === 'trial' ? 'Trial' : currentPlan === 'pro' ? 'Pro' : 'Enterprise'}
              </CardTitle>
              <CardDescription className="text-base mt-1">
                {currentPlan === 'trial' && (
                  <span>
                    {trialDaysRemaining} days remaining • Upgrade to Pro to continue after trial
                  </span>
                )}
                {currentPlan === 'pro' && (
                  <span>
                    Active subscription • {seatCount.totalPaid} paid user{seatCount.totalPaid !== 1 ? 's' : ''} in use
                  </span>
                )}
                {currentPlan === 'enterprise' && (
                  <span>
                    Enterprise plan • Contact your account manager for changes
                  </span>
                )}
              </CardDescription>
            </div>
            {currentPlan === 'trial' && (
              <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900">
                <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-300" />
                <AlertDescription className="text-amber-800 dark:text-amber-200">
                  Upgrade to Pro to keep access after trial ends
                </AlertDescription>
              </Alert>
            )}
          </div>

          {currentPlan === 'trial' && (
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Trial Progress</span>
                <span>{30 - trialDaysRemaining} / 30 days</span>
              </div>
              <Progress 
                value={(30 - trialDaysRemaining) / 30 * 100} 
                className="h-2"
              />
            </div>
          )}
        </CardHeader>
      </Card>

      {/* Pricing Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        <PricingCard
          tier="trial"
          isCurrentPlan={currentPlan === 'trial'}
          onSelectPlan={handleUpgradeToPro}
        />
        <PricingCard
          tier="pro"
          isCurrentPlan={currentPlan === 'pro'}
          isHighlighted={currentPlan === 'trial'}
          onSelectPlan={handleUpgradeToPro}
          monthlyCost={tempSeatCount * 20}
          features={[
            'Everything in Trial',
            `$${tempSeatCount * 20}/month for ${tempSeatCount} user${tempSeatCount !== 1 ? 's' : ''}`,
            'Users with Creator, Reviewer, or Approver roles',
            'Unlimited Viewer users (free)',
            'Up to 100 team members',
            'Priority support',
          ]}
        />
        <PricingCard
          tier="enterprise"
          isCurrentPlan={currentPlan === 'enterprise'}
          onSelectPlan={handleContactSales}
        />
      </div>

      {/* Seat Usage Summary */}
              <Card>
          <CardHeader>
            <CardTitle>User Usage</CardTitle>
            <CardDescription>
              {currentPlan === 'trial' 
                ? 'Preview your team user usage (upgrade to activate billing)'
                : 'Manage your team users and costs'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">Creators</div>
                <div className="text-2xl font-bold">{seatCount.creators}</div>
              </div>
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">Reviewers</div>
                <div className="text-2xl font-bold">{seatCount.reviewers}</div>
              </div>
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">Approvers</div>
                <div className="text-2xl font-bold">{seatCount.approvers}</div>
              </div>
              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">Viewers (Free)</div>
                <div className="text-2xl font-bold">{seatCount.viewers}</div>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Monthly Cost</span>
                <span className="text-2xl font-bold">
                  {formatCurrency(seatCount.totalPaid * 20)}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {seatCount.totalPaid} paid user{seatCount.totalPaid !== 1 ? 's' : ''} × $20/month = {formatCurrency(seatCount.totalPaid * 20)}/month
              </p>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleManageSeats}>
                {currentPlan === 'trial' ? 'Configure Users' : 'Manage Users'}
              </Button>
              {currentPlan !== 'trial' && (
                <Button variant="outline">
                  Download Invoice
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

      {/* Seat Management Dialog */}
      <SeatManagementDialog
        isOpen={isSeatDialogOpen}
        onClose={() => setIsSeatDialogOpen(false)}
        teamMembers={teamMembers}
        onSeatChange={handleSeatChange}
      />
    </div>
  );
}
